import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CatalogFacade } from '../../../facades/catalog.facade';
import { MathUtils } from '../../../utils/math.utils';
import { AnalyticsService } from '../../../analytics/analytics.service';
import { EProductDetails } from '../../../configurations/product-details';
import { ProductUtils } from '../../../utils/product.utils';
import { SlickCarouselComponent } from '../../../shared/slick-carousel/slick.component';
import { IMaterialMasterNumbersToggles, IPrice } from '../../../models/common.models';
import { skipWhile, take, takeUntil } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { combineLatest, Observable, Subject } from 'rxjs';
import { EFeatureToggles, EUserRoles, KG, KGM, LBS } from '../../../configurations/common';
import { IShsEquipmentData, ISimpleInstallBaseProduct } from '../../../models/installedbase.models';
import { ConfigurationFacade } from '../../../facades/configuration.facade';
import {
  IAbstractProductAvailability,
  IAbstractProductAvailabilityResource,
  IAbstractProductPrices,
  IAbstractProductPricesAndAvailability,
  ICachedProductSkuAndRequiredReload,
  IProductLabel,
} from '../../../models/abstract-product.models';
import { PriceUtils } from '../../../utils/price.utils';
import { IBaseConfig } from '../../../models/enviroment-delivery-details.model';
import { ICart } from '../../../models/cart.models';
import { CustomerFacade } from '../../../facades/customer.facade';
import { SparePartsUtils } from '../../../utils/spare-parts.utils';
import { Store } from '@ngrx/store';
import { State } from '../../../reducers';
import { CatalogActions } from '../../../actions';
import { MarketingFacade } from '../../../facades/marketing.facade';
import { IBaseWishlist } from '../../../models/wishlist.models';

@Component({
  selector: 'app-spare-part-product-pdp',
  templateUrl: './spare-part-product-pdp.component.html',
  styleUrls: ['./spare-part-product-pdp.component.scss'],
  standalone: false,
})
export class SparePartProductPdpComponent implements OnInit, OnDestroy, OnChanges {
  private readonly analyticsService: AnalyticsService = inject(AnalyticsService);
  private readonly catalogFacade: CatalogFacade = inject(CatalogFacade);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly configurationFacade: ConfigurationFacade = inject(ConfigurationFacade);
  private readonly customerFacade: CustomerFacade = inject(CustomerFacade);
  private readonly store: Store<State> = inject<Store<State>>(Store);
  private readonly marketingFacade: MarketingFacade = inject(MarketingFacade);

  openZoomedPicture: boolean = false;
  isQuantityValid: boolean = true;
  currentlyInStock: boolean = false;
  closeModal: boolean = false;
  isEligible: boolean = false;
  isExchangeable: boolean = false;
  isAvailable: boolean = false;
  pricesLoaded: boolean = false;
  emptyAttribute: string = '-';
  attributes: IBaseConfig[] = [];
  guestConst: EUserRoles = EUserRoles.Guest;
  isBusinessPartner: boolean = false;
  priceUtils: typeof PriceUtils = PriceUtils;
  pricingGroupForConsumables!: string;
  materialMasterToggles!: IMaterialMasterNumbersToggles;
  isProductDiscontinuedStatusEnabled: boolean = false;
  canCartItemsBeChanged: (currentCart: ICart, isBusinessPartner: boolean) => boolean = SparePartsUtils.canCartItemsBeChanged;

  @Input() systemDetails!: any;
  @Input() equipmentFlNumber!: string;
  @Input() stepNumber!: number;
  @Input() concreteProduct!: any;
  @Input() wishlists: IBaseWishlist[] = [];
  @Input() isWishlistsActive: boolean = false;
  @Input() isProductTagsActive: boolean = false;
  @Input() sku!: string;
  @Input() yourPrice!: IPrice | null | undefined;
  @Input() listPrice!: IPrice | null | undefined;
  @Input() productInfoLoaded!: boolean;
  @Input() concreteSku: string | null = null;
  @Input() abstractProduct!: any;
  @Input() labels: IProductLabel[] = [];
  @Input() defaultImg!: string;
  @Input() largeUrls: string[] = [];
  @Input() addNewModalActive!: boolean;
  @Input() productQuantity!: number;
  @Input() isAddToCartInProgress$: Observable<boolean> = new Observable<boolean>();
  @Input() smallUrls: string[] = [];
  @Input() mainPicture!: string;
  @Input() productDetailsEnum: typeof EProductDetails = EProductDetails;
  @Input() installBaseProducts: ISimpleInstallBaseProduct[] = [];
  @Input() displayConfig: EProductDetails[] = [];
  @Input() loadingCartDataInProgress!: boolean;
  @Input() wasRedirectedFromEquipmentPage!: boolean;
  @Input() companyRoles: EUserRoles[] = [];
  @Input() agreement!: IShsEquipmentData;
  @Input() recentlyOpenedProducts: ICachedProductSkuAndRequiredReload[] = [];
  @Input() isDeliveryAvailabilityEnabled: boolean = false;

  @Output() addNewModalWishlist: EventEmitter<void> = new EventEmitter<void>();
  @Output() addProductToCartSubmit: EventEmitter<void> = new EventEmitter<void>();
  @Output() productQuantityChange: EventEmitter<number> = new EventEmitter<number>();

  @ViewChild(SlickCarouselComponent) slickCarouselComponent!: SlickCarouselComponent;

  currentCart!: ICart;
  isSapP40Enabled: boolean = false;
  loadedSuccessfully: boolean = true;
  isProductDiscontinuedStatusEnabled$: Observable<boolean> = new Observable<boolean>();
  isMyInstalledBaseFlowEnabled$: Observable<boolean> = new Observable<boolean>();
  private readonly unsubscribe$: Subject<void> = new Subject<void>();

  sparePartsDetails: string[] = [
    'sap_country_of_origin',
    'sap_p40_modality_description',
    'material_number',
    'sap_p40_eos',
    'sap_p40_material_pricing_group_3_mapped',
    'sap_gtin',
    'mcm_webshop_anatomic_area_mapped',
    'sap_p40_proratio_material',
    'sap_p40_alternate_material',
    'sap_p40_subsequent_material',
  ];

  ngOnInit(): void {
    this.isProductDiscontinuedStatusEnabled$ = this.configurationFacade.isFeatureEnabled(EFeatureToggles.PRODUCT_DISCONTINUED_STATUS);
    this.isMyInstalledBaseFlowEnabled$ = this.configurationFacade.isFeatureEnabled(EFeatureToggles.MY_INSTALLED_BASE_FLOW);

    this.getAttributes();
    this.selectIsCustomerBusinessPartner();
    this.shouldShowWarning();
    this.getMaterialMasterNumbersToggles();
    this._selectIsDiscontinuedStatusEnabled();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.abstractProduct?.previousValue?.sku !== changes?.abstractProduct?.currentValue?.sku) {
      this.getPricesAndAvailabilitiesWhenCartIsAvailable();
    }
  }

  /**
   * Method for showing warning message
   */
  shouldShowWarning(): void {
    if (!this.currentCart?.attributes?.soldToAddress && this.isBusinessPartner && (!!this.systemDetails?.siemensEquipmentId && !!this.systemDetails?.materialNumber && !!this.systemDetails?.nameEnUs)) {
      this.showWarning();
    }
  }

  isAddToCartButtonDisabled(): boolean {
    return !this.isSapP40Enabled
      || !this.isQuantityValid
      || this.loadingCartDataInProgress
      || (!this.pricesLoaded && !!this.equipmentFlNumber)
      || !this.loadedSuccessfully
      || (this.isProductDiscontinued() && !this.isAvailable);
  }

  addToWishlist(): void {
    this.addNewModalWishlist.emit();
  }

  setLargeUrl(index: number): void {
    this.mainPicture = this.largeUrls[index];
    this.slickCarouselComponent.slickGoTo(index);
  }

  isProductWithMaterialMasterNumber(): boolean {
    return (
      (this.materialMasterToggles?.peakNumber &&
        this.abstractProduct?.attributes?.sap_p40_peak_flag?.toUpperCase() === 'X') ||
      (this.materialMasterToggles?.notificationNumber &&
        this.abstractProduct?.attributes?.sap_p40_notif_flag?.toUpperCase() === 'X')
      );
  }

  isConfigurable(): boolean {
    return this.concreteProduct ? this.concreteProduct.attributes.productConfigurationInstance !== null : false;
  }

  changeProductQuantity(productQuantity: number | null): void {
    this.checkIfPositiveInteger(productQuantity);
    if (this.isQuantityValid) {
      this.productQuantityChange.emit(productQuantity ?? 0);
    }
  }

  private checkIfPositiveInteger(productQuantity: number | null): void {
    this.isQuantityValid = MathUtils.checkIfNumeric(productQuantity) && (productQuantity ?? 0) > 0;
  }

  addProductToCart(): void {
    this.addProductToCartSubmit.emit();
  }

  openZoomPicture(): void {
    if (this.mainPicture !== this.defaultImg) {
      this.openZoomedPicture = !this.openZoomedPicture;
      if (this.openZoomedPicture) {
        this.analyticsService.setProducts(this.abstractProduct);
        this.analyticsService.trackProduct('image.enlarge', this.abstractProduct);
      }
    }
  }

  getDetailAttribute(attributeValue: string | null): string {
    return ProductUtils.getDetailAttribute(attributeValue);
  }

  tracking(name: string): void {
    this.analyticsService.setProducts(this.abstractProduct);
    this.analyticsService.trackAccordion(name);
  }

  trackInstalledBase(installedBase: ISimpleInstallBaseProduct): void {
    this.catalogFacade.setInstalledBase(installedBase);
    this.analyticsService.trackInstalledBase(installedBase.attributes.materialName, installedBase.id ?? '');
  }

  displayProductDetail(section: EProductDetails): boolean {
    return this.displayConfig.includes(section);
  }

  hasTranslation(key: string): boolean {
    const translation: string = this.translate.instant(key);
    return !!translation && translation !== key;
  }

  getWeightDetails(): string {
    const attrs: any = this.abstractProduct?.attributes ?? {};

    let weightP40: string = '';
    let unitP40: string = '';
    if (attrs?.sap_p40_gross_weight != null && attrs?.sap_p40_gross_weight !== '') {
      unitP40 = attrs?.sap_p40_uom_weight === KGM ? KG : LBS;
      weightP40 = `${attrs.sap_p40_gross_weight} ${unitP40}`;
    }

    let weight: string = '';
    if (attrs?.sap_gross_weight != null && attrs?.sap_gross_weight !== '') {
      const unit: string = attrs?.sap_gross_weight_unit === KGM ? KG : LBS;
      if (unitP40 !== unit) {
        weight = `${attrs.sap_gross_weight} ${unit}`;
      }
    }

    if (!weightP40 && !weight) {
      return '-';
    }
    return weightP40 && weight ? `${weightP40} / ${weight}` : weightP40 || weight;
  }

  openSparePartsViewer(): void {
    const productMaterialNumber: any = this.abstractProduct.attributes?.material_number;
    const sysIvk: string = this.systemDetails?.materialNumber;
    const sparePartsLink: string = sysIvk
      ? `${environment.sparePartsViewerUrl}&sysIVK=${sysIvk}&matnum=${productMaterialNumber}`
      : `${environment.sparePartsViewerUrl}&matnum=${productMaterialNumber}`;
    window.open(sparePartsLink, '_blank');
  }

  toggleAttribute(attribute: string): boolean {
    return this.attributes.find((attr: IBaseConfig): boolean => attr.name === attribute)?.value === 'true';
  }

  private getAttributes(): void {
    this.configurationFacade.getTranslationByKey(['spare-parts.attributes'])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(data => {
        Object.entries(data['spare-parts.attributes']).forEach(([key, attribute]: [string, any]) => {
          this.attributes.push({
            name: key,
            value: attribute,
          });
        });
      });
  }

  /**
   * This method selects loading flag form catalog state and wait until flag is not false.
   * This is required because we have to wait until product abstract data (prices and availabilities) are loaded.
   * This logic is required cause is not necessary to call API every time.
   */
  selectPricesLoadingAndBasedOnItLoadProductAbstractData(): void {
    this.catalogFacade.selectArePricesAndAvailabilitiesLoading()
      .pipe(
        skipWhile((loading: boolean): boolean => loading === true),
        takeUntil(this.unsubscribe$),
      ).subscribe((loading: boolean) => {
      this.pricesLoaded = !loading;
      this.selectPricesAndAvailabilitiesAndProcessData();
    });
  }

  /**
   * This method waits until the cart is loaded,
   * checks whether SAP P40 is enabled and gets a pricing group for consumables.
   * When the cart is loaded and all observables emitted a value, the getPricesAndAvailabilities method is called.
   */
  getPricesAndAvailabilitiesWhenCartIsAvailable(): void {
    combineLatest([
      this.marketingFacade.selectCart(),
      this.configurationFacade.isFeatureEnabled(EFeatureToggles.SAP_P40),
      this.catalogFacade.getPricingGroupForConsumables()
    ]).pipe(
      skipWhile(([cart]) => cart === null),
      takeUntil(this.unsubscribe$),
    ).subscribe(([cart, isFeatureEnabled, pricingGroup]: [ICart, boolean, string]) => {
      if (cart.relationships) {
        this.currentCart = cart;
        this.isSapP40Enabled = isFeatureEnabled;
        this.pricingGroupForConsumables = pricingGroup;
        this.getPricesAndAvailabilities();
      }
    });
  }

  /**
   * In case loading prices and availabilities from API is required then
   * this.getPricesAndAvailabilitiesFrom SAP method is called
   */
  getPricesAndAvailabilities(): void {
    if (this.isSapP40Enabled && this.checkIfProductIsAvailableForOnlinePurchase()) {
      if (this.currentCart?.id && this.currentCart.attributes?.systemDetails?.siemensEquipmentId) {
        if (this.isGettingProductDataFromApiRequired()) {
          this.getPriceAndAvailabilityFromSAP();
        }
        this.selectPricesLoadingAndBasedOnItLoadProductAbstractData();
      }
    }
  }

  /**
   * Method for dispatching of getting product abstract prices and availabilities from API action
   */
  getPriceAndAvailabilityFromSAP(): void {
    this.store.dispatch(CatalogActions.loadAbstractProductInfoFromSap({sku: this.sku, uuid: this.currentCart.id}));
  }

  /**
   * Method for selecting and processing prices and availability from storage
   */
  selectPricesAndAvailabilitiesAndProcessData(): void {
    let productPrices: IAbstractProductPrices;
    let productAvailabilities: IAbstractProductAvailability;
    this.catalogFacade.selectAbstractProductPricesAndAvailabilitiesBySku(this.sku)
      .pipe(
        skipWhile((productPrices: IAbstractProductPricesAndAvailability): boolean => productPrices[0].id !== this.sku),
        takeUntil(this.unsubscribe$),
      ).subscribe({
      next: (pricesAndAvailabilities: IAbstractProductPricesAndAvailability) => {
        productPrices = pricesAndAvailabilities[0];
        productAvailabilities = pricesAndAvailabilities[1];
        if (
          productPrices?.attributes.loadedSuccessfully
          && productAvailabilities?.attributes.loadedSuccessfully
        ) {
          this.loadedSuccessfully = true;
          this.isAvailable = productAvailabilities?.attributes?.availability;
          this.setListPrice(productPrices);
          this.setYourPrice(productPrices);
          this.setProductAvailabilityAttributes(productAvailabilities);
        }
        // if cart is without FL (system details.siemensEquipmentId), error should not be displayed
        else if (this.currentCart?.attributes?.systemDetails?.siemensEquipmentId) {
          this.handlePriceOrAvailabilityLoadingFailure(productPrices, productAvailabilities);
        }
      },
      error: () => {
        this.handlePriceOrAvailabilityLoadingFailure(productPrices, productAvailabilities);
      },
    });
  }

  /**
   * This method try to find current product's sku in cached products skus list and depends on flag "isReloadRequired"
   * decide whether to load product abstract data from sap or from cached state
   * @returns {boolean}
   */
  isGettingProductDataFromApiRequired(): boolean {
    const currentProduct: ICachedProductSkuAndRequiredReload | undefined = this.recentlyOpenedProducts.find((item: ICachedProductSkuAndRequiredReload): boolean => item.sku === this.sku);
    return currentProduct?.isReloadRequired ?? true;
  }

  isUserSparePartsViewer(): boolean {
    return this.companyRoles.includes(EUserRoles.SPCViewer);
  }

  private handlePriceOrAvailabilityLoadingFailure(
    productPrices: IAbstractProductPrices,
    productAvailabilities: IAbstractProductAvailabilityResource,
  ): void {
    this.loadedSuccessfully = false;

    if (productPrices) {
      this.setListPrice(productPrices);
      this.setYourPrice(productPrices);
    }
    if (productAvailabilities) {
      this.setProductAvailabilityAttributes(productAvailabilities);
    }
    this.translate.get([
      'spare-parts.pdp-pricing-availability-failed-loading-msg',
      'spare-parts.pdp-pricing-availability-failed-loading-msg-type',
    ]).pipe(take(1))
      .subscribe((translations: any) => {
        const message: any = translations['spare-parts.pdp-pricing-availability-failed-loading-msg'];
        const type: any = translations['spare-parts.pdp-pricing-availability-failed-loading-msg-type'];
        this.configurationFacade.setAlert({message, type});
      });
  }

  private setListPrice(productPrices: IAbstractProductPrices): void {
    const prices: any = productPrices.attributes.prices;
    const guestPrice: IPrice | null | undefined = this.priceUtils.getGuestPrice(prices);
    const defaultPrice: IPrice | null | undefined = this.priceUtils.getDefaultPrice(prices);
    // assume the default price === list price if it's the only price returned in the response
    this.listPrice = guestPrice ?? defaultPrice;
  }

  private setYourPrice(productPrices: IAbstractProductPrices): void {
    const prices: any = productPrices.attributes.prices;
    const defaultPrice: IPrice | null | undefined = this.priceUtils.getDefaultPrice(prices);
    this.yourPrice = defaultPrice ?? null;
  }

  private setProductAvailabilityAttributes(productAvailabilities: IAbstractProductAvailabilityResource): void {
    this.isExchangeable = productAvailabilities.attributes.exchangeableItem;
    this.isEligible = productAvailabilities.attributes.eligible;
    this.currentlyInStock = productAvailabilities.attributes.availability;
  }

  public updatedPricesAfterChangeSoldTo(value: boolean): void {
    if (value) {
      this.getPricesAndAvailabilities();
    }
  }

  /**
   *
   * @private
   * Method for creating warning window
   */
  private showWarning(): void {
    this.configurationFacade.appendNotification({
      type: 'warning',
      title: 'issue-with-data.title',
      messages: [{
        key: 'issue-with-data.message',
      }],
    });
  }

  selectIsCustomerBusinessPartner(): void {
    this.customerFacade.isBusinessPartner().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(isBusinessPartner => {
      this.isBusinessPartner = isBusinessPartner;
    });
  }

  /**
   * Check if product is discontinued (sap_p40_sales_status or sap_sales_status is '01' or '02')
   *
   * @return {boolean}
   */
  isProductDiscontinued(): boolean {
    const salesStatus: any = this.abstractProduct?.attributes?.sap_p40_sales_status;
    const sapSalesStatus: any = this.abstractProduct?.attributes?.sap_sales_status;
    return (salesStatus === '01' || salesStatus === '02' || sapSalesStatus === '01' || sapSalesStatus === '02');
  }

  /**
   * Get if discontinued status functionality is enabled (from arakh config)
   */
  private _selectIsDiscontinuedStatusEnabled(): void {
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.PRODUCT_DISCONTINUED_STATUS).pipe(take(1)).subscribe(isProductDiscontinuedStatusEnabled => {
      this.isProductDiscontinuedStatusEnabled = isProductDiscontinuedStatusEnabled;
    });
  }

  /**
   * Check if discontinued info should be displayed
   * Info window is shown only after product price is loaded
   *
   * @return {boolean}
   */
  isDiscontinuedInfoDisplayed(): boolean {
    return (this.isProductDiscontinuedStatusEnabled && this.isProductDiscontinued() && !this.currentlyInStock && this.pricesLoaded && this.loadedSuccessfully);
  }

  /**
   * Check if product has subsequent material
   *
   * @return {boolean}
   */
  isSubsequentMaterialAvailable(): boolean {
    return !!((this.abstractProduct?.attributes?.sap_p40_sales_status === '02' || this.abstractProduct?.attributes?.sap_sales_status === '02')
      && this.abstractProduct?.attributes?.sap_p40_subsequent_material);
  }

  /**
   * Get subsequent material for product
   * Handle multiple cases: no subsequent material/one subsequent material
   * Add leading zeros to subsequent material number if needed
   *
   * @return {string[]}
   */
  getSubsequentMaterial(): string[] {
    if (this.isSubsequentMaterialAvailable()) {
      return [this.abstractProduct.attributes.sap_p40_subsequent_material.padStart(8, '0')];
    }

    return [];
  }

  /**
   * Returns true if product is available for online purchase
   *  user has to be non-business partner
   *  FL has to be selected
   *  product is available for online purchase
   *    if product is consumable and system details is not isSparePartsAvailable
   *    or system details is isSparePartsAvailable
   *
   * @return {boolean}
   */
  checkIfProductIsAvailableForOnlinePurchase(): boolean {
    if (
      !( // check if user is non-BP and FL is selected
        this.companyRoles.includes(EUserRoles.BusinessPartnerBuyer)
        || this.companyRoles.includes(EUserRoles.BusinessPartnerApprover)
      ) && this.currentCart?.attributes?.systemDetails?.siemensEquipmentId
    ) {
      return ( // check if product is available for online purchase
        this.abstractProduct.attributes.sap_p40_material_pricing_group_3 === this.pricingGroupForConsumables
        && !this.currentCart?.attributes.systemDetails.isSparePartsAvailable
      ) || this.currentCart?.attributes.systemDetails.isSparePartsAvailable;
    } else {
      return true;
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private getMaterialMasterNumbersToggles(): void {
    this.marketingFacade.getMaterialMasterNumbersToggles()
      ?.pipe(take(1))
      .subscribe((materialMasterToggles) => {
        this.materialMasterToggles = materialMasterToggles;
        for (const [key, value] of Object.entries(this.materialMasterToggles)) {
          if (key === 'softwareVersion' || key === 'serialNumber' || key === 'peakNumber' || key === 'notificationNumber') {
            this.materialMasterToggles[key] = value === true || value === 'true';
          }
        }
      });
  }

}
